import { orZero } from 'javascripts/util/orZero';
import { cloneDeep } from 'lodash';

export interface Performance {
  /**
   * The effort that has been committed to an iteration or user. This should not
   * change after the iteration has started.
   */
  committed: Effort;
  /**
   * The effort that has been added to an iteration or user after committment.
   */
  added: Effort;
  /**
   * The effort that has been removed from an iteration or user after committment.
   */
  removed: Effort;
  /**
   * The effort that has been completed in an iteration or by a user.
   */
  completed: Effort;
  /**
   * The effort that remains incomplete in an iteration or user.
   */
  incomplete: Effort;

  clone(): Performance;
}

export interface EffortValue {
  count: number;
  originalEstimate: number;
  remainingEstimate: number;
  workDone: number;
}

export type EffortValueKey = keyof EffortValue & string;

/**
 * This is used to represent the effort of a user in a sprint. Each effort has a
 * count, original estimate, and remaining estimate, meaning for each bucket of
 * performance we can perform operations on everything at once.
 *
 * You might be wondering why Effort has a name. That's to make debugging
 * easier, write to a console with the name and the changes
 */
export default class Effort {
  public count: number;
  public originalEstimate: number;
  public remainingEstimate: number;
  public workDone: number;

  constructor(
    private readonly name: string,
    value?: Partial<EffortValue> | Effort
  ) {
    this.count = orZero(value?.count);
    this.originalEstimate = orZero(value?.originalEstimate);
    this.remainingEstimate = orZero(value?.remainingEstimate);
    this.workDone = orZero(value?.workDone);
  }

  get value(): EffortValue {
    return {
      count: this.count,
      originalEstimate: this.originalEstimate,
      remainingEstimate: this.remainingEstimate,
      workDone: this.workDone,
    };
  }

  get(key: EffortValueKey): number {
    return this[key];
  }

  /**
   * Add another effort or individual values. Mutates in place
   */
  add(value: Partial<EffortValue>): this;
  add(other: Effort): this;
  add(valueOrOther: Partial<EffortValue> | Effort): this {
    this.count += orZero(valueOrOther.count);
    this.originalEstimate += orZero(valueOrOther.originalEstimate);
    this.remainingEstimate += orZero(valueOrOther.remainingEstimate);
    this.workDone += orZero(valueOrOther.workDone);

    return this;
  }

  // Subtract another effort or individual values. Mutates in place
  subtract(value: Partial<EffortValue>): this;
  subtract(other: Effort): this;
  subtract(valueOrOther: Partial<EffortValue> | Effort): this {
    const other =
      valueOrOther instanceof Effort
        ? valueOrOther
        : new Effort(this.name, valueOrOther);
    return this.add(other.invert());
  }

  /**
   * Return a new effort with the values inverted (negated)
   */
  invert(): Effort {
    return new Effort(this.name, {
      count: -this.count,
      originalEstimate: -this.originalEstimate,
      remainingEstimate: -this.remainingEstimate,
      workDone: -this.workDone,
    });
  }

  /**
   * Clone and return
   */
  clone() {
    return new Effort(this.name, cloneDeep(this.value));
  }
}
